import { setBulkKeywordMappingModalDisplay } from "@/features/modalSlice";
import { Button, Modal, Select, Form } from "antd";
import React, { useMemo, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    IMainContext,
    TClientTags,
    VcpmColumns,
} from "@/ts/common-types";
import { find } from "underscore";
import { showToast } from "@/ts/modals";
import { addKeywordMappings, internationalizeNumericValue } from "@/ts/utils";
// import { addKeywordMappings, internationalizeNumericValue, getFilteredKeywordIds } from "@/ts/utils";
import { mainContext } from "@/ts/constants";
import { KeywordMappingOperation } from "@/ts/common-types";
import * as Sentry from "@sentry/react";

type IBulkKeywordMappingModalProps = {
    show: boolean;
};

const BulkKeywordMappingModal: React.FC<IBulkKeywordMappingModalProps> = ({
    show,
}) => {
    const dispatch = useDispatch();
    const { keywordMappingGridApi } = useContext<IMainContext>(mainContext);
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);

    const keywordMappingSelectionData = useSelector(
        (state: { keywordMappingSelectionData: { value: any[] } }) =>
            state.keywordMappingSelectionData.value
    );

    const keywordMappingAllPagesSelected = useSelector(
        (state: { keywordMappingAllPagesSelected: { value: boolean } }) =>
            state.keywordMappingAllPagesSelected.value
    );

    const bulkKeywordMappingCount = useSelector(
        (state: { bulkKeywordMappingCount: { value: number } }) =>
            state.bulkKeywordMappingCount.value
    );

    const keywordMappingFilterModel = useSelector(
        (state: { keywordMappingFilterModel: { value: any } }) =>
            state.keywordMappingFilterModel.value
    );

    const tagTypeList = useSelector(
        (state: { tagTypeList: { value: TClientTags[] } }) =>
            state.tagTypeList.value
    );

    const avlCategories = useMemo(() => {
        const categoryTag = find(
            tagTypeList,
            (tag) => tag.tag_type === VcpmColumns.CATEGORY
        );

        if (!categoryTag || !Array.isArray(categoryTag.tags_map)) return [];

        return categoryTag.tags_map.map((tagStr) => {
            const [id, name] = tagStr.split("##");
            return {
                value: id,
                label: name,
            };
        });
    }, [tagTypeList]);

    const avlSubCategories = useMemo(() => {
        const subCategoryTag = find(
            tagTypeList,
            (tag) => tag.tag_type === VcpmColumns.SUBCATEGORY
        );

        if (!subCategoryTag || !Array.isArray(subCategoryTag.tags_map)) return [];

        return subCategoryTag.tags_map.map((tagStr) => {
            const [id, name] = tagStr.split("##");
            return {
                value: id,
                label: name,
            };
        });
    }, [tagTypeList]);

    const avlBrands = useMemo(() => {
        const brandsTag = find(
            tagTypeList,
            (tag) => tag.tag_type === VcpmColumns.BRAND
        );

        if (!brandsTag || !Array.isArray(brandsTag.tags_map)) return [];

        return brandsTag.tags_map.map((tagStr) => {
            const [id, name] = tagStr.split("##");
            return {
                value: id,
                label: name,
            };
        });
    }, [tagTypeList]);

    const handleClose = () => {
        form.resetFields();
        dispatch(setBulkKeywordMappingModalDisplay(false));
    };

    interface KeywordMappingFormValues {
        category?: string[];
        subcategory?: string[];
        brand?: string;
    }

    const handleSubmit = async (values: KeywordMappingFormValues) => {
        setIsLoading(true);
        const { category, subcategory, brand } = values;

        try {
            const updateConfig = {
                ...(keywordMappingAllPagesSelected 
                    ? { filterModel: keywordMappingFilterModel }
                    : keywordMappingSelectionData.length 
                        ? { keywords: keywordMappingSelectionData.map(data => data.sys_keyword_id) }
                        : { filterModel: keywordMappingFilterModel }
                ),
                category_list: category?.map(Number) || [],
                subcategory_list: subcategory?.map(Number) || [],
                brand_list: brand ? [Number(brand)] : [],
                operation: KeywordMappingOperation.UPSERT
            };

            const response = await addKeywordMappings(updateConfig);
            const responseObj = (response as unknown) as { status: boolean; message?: string } | null;

            if (!responseObj || !responseObj.status) {
                const errorMessage = responseObj?.message || 'Failed to update keyword mappings';
                throw new Error(errorMessage);
            }

            const successMessage = responseObj.message || 'Successfully updated keyword mappings';
            showToast("success", successMessage);

            // Refresh the grid data
            if (keywordMappingGridApi) {
                keywordMappingGridApi.refreshServerSideStore({
                    route: [],
                    purge: true,
                });
            }

            form.resetFields();
            dispatch(setBulkKeywordMappingModalDisplay(false));
        } catch (err: any) {
            showToast("danger", err.message);
            Sentry.captureException(err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            open={show}
            centered
            title={
                <div>
                    <div className="d-flex align-items-center pb-2"
                        style={{ fontSize: "1.5rem", fontWeight: "600" }}>
                        Add Keyword Mapping
                    </div>
                    <div className="d-flex align-items-center"
                        style={{
                            fontSize: "0.8rem",
                            color: "#666",
                            fontWeight: "400",
                            backgroundColor: "#f5f5f5",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            width: "fit-content",
                        }}>
                        Selected Keywords:{" "}
                        <span style={{ marginLeft: "6px", fontWeight: "600" }}>
                            {internationalizeNumericValue(
                                keywordMappingAllPagesSelected
                                    ? bulkKeywordMappingCount
                                    : keywordMappingSelectionData.length || bulkKeywordMappingCount
                            )}
                        </span>
                    </div>
                </div>
            }
            onCancel={handleClose}
            footer={[
                <Button key="back" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={() => form.submit()}
                    loading={isLoading}
                >
                    Submit
                </Button>,
            ]}
            width="630px"
        >
            <Form
                form={form}
                layout="horizontal"
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
                onFinish={handleSubmit}
            >
                <Form.Item
                    label={VcpmColumns.CATEGORY}
                    name="category"
                    className="mt-3"
                >
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        placeholder="Select categories"
                        options={avlCategories}
                        filterSort={(optionA, optionB) =>
                            (optionA?.label ?? "").toLowerCase()
                                .localeCompare((optionB?.label ?? "").toLowerCase())
                        }
                        style={{ width: '100%' }}
                        tagRender={({ label, closable, onClose }) => (
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                borderRadius: 4,
                                padding: '2px 8px',
                                marginRight: 4,
                                display: 'inline-flex',
                                alignItems: 'center'
                            }}>
                                {label}
                                {closable && (
                                    <span onClick={onClose} style={{ marginLeft: 4, cursor: 'pointer' }}>×</span>
                                )}
                            </div>
                        )}
                    />
                </Form.Item>

                <Form.Item
                    label={VcpmColumns.SUBCATEGORY}
                    name="subcategory"
                >
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        placeholder="Select subcategories"
                        options={avlSubCategories}
                        filterSort={(optionA, optionB) =>
                            (optionA?.label ?? "").toLowerCase()
                                .localeCompare((optionB?.label ?? "").toLowerCase())
                        }
                        style={{ width: '100%' }}
                        tagRender={({ label, closable, onClose }) => (
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                borderRadius: 4,
                                padding: '2px 8px',
                                marginRight: 4,
                                display: 'inline-flex',
                                alignItems: 'center'
                            }}>
                                {label}
                                {closable && (
                                    <span onClick={onClose} style={{ marginLeft: 4, cursor: 'pointer' }}>×</span>
                                )}
                            </div>
                        )}
                    />
                </Form.Item>

                <Form.Item
                    label={VcpmColumns.BRAND}
                    name="brand"
                >
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        placeholder="Select brand"
                        options={avlBrands}
                        filterSort={(optionA, optionB) =>
                            (optionA?.label ?? "").toLowerCase()
                                .localeCompare((optionB?.label ?? "").toLowerCase())
                        }
                        style={{ width: '100%' }}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default BulkKeywordMappingModal;